<template lang="pug">
    main.main

        Header(ref='header')

        router-view

        //- vue-progress-bar

        //- Updated

</template>

<script>
/* https://github.com/chris-pearce/backpack.css */
import 'backpack.css';

/* meta info (for title) */
import meta from '@/assets/data/meta';

/* 'new content available' badge */
// const Updated = () => import('@/components/Updated.vue');

const Header = () => import('@/components/Header.vue');

export default {
    name: 'App',

    metaInfo: {
        titleTemplate: titleChunk => {
            return titleChunk ? `${titleChunk} - ${meta.title}` : meta.title;
        }
    },

    components: {
        // Updated,
        Header
    },

    computed: {
        theme() {
            return this.$store.state.theme;
        }
    },

    watch: {
        theme(newValue, oldValue) {
            if (newValue !== oldValue) document.documentElement.style.setProperty('--logoColor', newValue);
        }
    },

    created() {
        this.removeLoadingClass();

        this.$http.interceptors.response.use(undefined, err => {
            return new Promise(() => {
                if (err.status === 401 && err.config && !err.config.__isRetryRequest) {
                    this.$store.dispatch('logout');
                }
                throw err;
            });
        });
    },

    mounted() {
        this.$Progress.finish();

        this.calculateVH();
    },

    methods: {
        progressOnRouting() {
            this.$Progress.start();

            this.$router.beforeEach((to, from, next) => {
                this.$Progress.start();
                next();
            });

            this.$router.afterEach(() => {
                this.$Progress.finish();
            });
        },

        removeLoadingClass() {
            const listener = () => {
                document.body.classList.remove('js-loading');
                window.removeEventListener('load', listener);
            };
            window.addEventListener('load', listener);
        },

        calculateVH() {
            const setVH = () => {
                const vh = window.innerHeight * 0.01;
                document.documentElement.style.setProperty('--vh', `${vh}px`);
            };

            setVH();

            /* resize */
            (function() {
                const throttle = function(type, name, obj) {
                    obj = obj || window;
                    let running = false;
                    const func = function() {
                        if (running) {
                            return;
                        }
                        running = true;
                        requestAnimationFrame(function() {
                            obj.dispatchEvent(new CustomEvent(name));
                            running = false;
                        });
                    };
                    obj.addEventListener(type, func);
                };
                throttle('resize', 'optimizedResize');
            })();
            window.addEventListener('optimizedResize', setVH);
        }
    }
};
</script>

<style lang="scss">
/* import npm styles (if needed) */
@import '~poly-fluid-sizing';

/* import fonts (only in App.vue) */
@import 'assets/css/fonts.scss';

/* import vars (repeat in all .vue files) */
@import 'assets/css/vars.scss';

@import 'assets/css/base.scss';

/* import swiper styles */
@import 'swiper/swiper-bundle.css';

a {
    color: inherit;
    font-weight: 500;
    transition: color 0.35s ease;

    &:hover {
        color: var(--color-main-hover);
        transition-duration: 0.15s;
    }

    .h1 & {
        font-weight: 600;

        &:hover {
            color: inherit;
            opacity: 0.8;
        }
    }
}

p.p {
    margin-bottom: 1em;
}

ol {
    &,
    li {
        list-style-type: decimal;
    }

    margin-left: 1em;
}

button,
input,
optgroup,
select,
textarea {
    line-height: 1.25;
}

/* reduce motion */
@media screen and (prefers-reduced-motion: reduce), (update: slow) {
    * {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001ms !important;
    }
}

/* start animation when js loaded */
body.js-loading *,
body.js-loading *::before,
body.js-loading *::after {
    animation-play-state: paused !important;
}

/* main styles */
html {
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    font-family: var(--font-main);
    font-weight: 400;
    font-size: 18px;
    line-height: 1.4;
    background: var(--color-main-bg);
    color: var(--color-main-text);
    font-variant-numeric: tabular-nums;
    overflow-anchor: auto;

    $mapFontSize: (
        320px: 15px,
        1440px: 18px,
        1920px: 25px
    );
    @include poly-fluid-sizing('font-size', $mapFontSize);
}
body {
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    font-family: var(--font-main);
    font-weight: 400;
    font-size: 18px;
    line-height: 1.4;
    background: var(--color-main-bg);
    color: var(--color-main-text);
    min-height: 100vh;
    font-variant-numeric: tabular-nums;
    overflow-anchor: auto;

    $mapFontSize: (
        320px: 15px,
        1440px: 16px,
        1920px: 21px
    );
    @include poly-fluid-sizing('font-size', $mapFontSize);
}

a,
button {
    outline: none !important;
}

.main {
    position: relative;
    margin: 0;
    padding-top: constant(safe-area-inset-top);
    padding-right: constant(safe-area-inset-right);
    padding-bottom: constant(safe-area-inset-bottom);
    padding-left: constant(safe-area-inset-left);
    min-height: 100vh;
    background: var(--color-accent-bg);
}
</style>
